import classNames from 'classnames';
import { type ReactNode, useState } from 'react';
import { ActionMaterialIcon } from '@/common/components/shared/action-material-icon/ActionMaterialIcon';
import { useScreenBreakpoint } from '@/common/hooks/shared/useScreenBreakpoint';

interface Props {
  /** Full Tailwind class string applied when expanded; Tailwind's scanner needs literal class names, not interpolated values */
  expandedMaxHeightClassName: string;
  /** Full Tailwind class string applied when collapsed (default `max-h-16`); a literal for the same reason. */
  collapsedMaxHeightClassName?: string;
  children: ReactNode;
}

/**
 * Collapsible filter panel that hides/shows content via a toggle arrow button.
 * On small screens (below md breakpoint) the filter starts collapsed to save space.
 */
export const CollapsibleFilter = ({
  children,
  expandedMaxHeightClassName,
  collapsedMaxHeightClassName = 'max-h-16',
}: Props) => {
  const isMediumScreen = useScreenBreakpoint('md');
  const [isFilterCollapsed, setFilterCollapsed] = useState(!isMediumScreen);

  return (
    <div className="flex">
      <div
        className={classNames('flex-1 overflow-y-auto md:overflow-y-hidden transition-[max-height] duration-200', {
          [collapsedMaxHeightClassName]: isFilterCollapsed,
          'max-h-[25dvh]': !isFilterCollapsed,
          [expandedMaxHeightClassName]: !isFilterCollapsed,
        })}
      >
        {children}
      </div>
      <div className="pt-4 pr-1 -ml-4">
        <ActionMaterialIcon
          iconName="keyboard_double_arrow_down"
          className={classNames('text-blue-7 transition-transform', { 'rotate-180': !isFilterCollapsed })}
          onClick={() => setFilterCollapsed(prev => !prev)}
        />
      </div>
    </div>
  );
};
